import { useCallback, useRef } from 'react';

type TextFieldElement = HTMLInputElement | HTMLTextAreaElement;

interface RegisteredField {
  element: TextFieldElement | null;
  setValue: (value: string) => void;
}

/** Props da spalmare su un `<Input>`/`<Textarea>` per renderlo bersaglio dell'inserimento. */
export interface VariableTargetProps {
  ref: (element: TextFieldElement | null) => void;
  onFocus: () => void;
  onBlur: () => void;
  onSelect: () => void;
  onKeyUp: () => void;
  onClick: () => void;
}

/**
 * Inserimento di un placeholder alla posizione del cursore nell'ULTIMO campo di
 * testo focalizzato.
 *
 * Il click sul pulsante della variabile fa perdere il focus al campo: la
 * selezione viene quindi memorizzata a ogni interazione (focus/select/keyup/
 * click/blur) e riusata al momento dell'inserimento. Dopo l'update il focus e il
 * caret vengono ripristinati subito dopo il commit di React (`requestAnimationFrame`),
 * col cursore appena dopo il testo inserito, così si possono incollare più
 * variabili di fila senza tornare a mano nel campo.
 */
export const useVariableInsertion = () => {
  const fieldsRef = useRef<Record<string, RegisteredField>>({});
  const activeFieldRef = useRef<string | null>(null);
  const selectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  const rememberSelection = useCallback((name: string) => {
    const field = fieldsRef.current[name];
    const element = field?.element;
    if (!element) return;
    activeFieldRef.current = name;
    selectionRef.current = {
      start: element.selectionStart ?? element.value.length,
      end: element.selectionEnd ?? element.value.length,
    };
  }, []);

  const registerTarget = useCallback(
    (name: string, setValue: (value: string) => void): VariableTargetProps => ({
      ref: (element) => {
        fieldsRef.current[name] = { element, setValue };
      },
      onFocus: () => rememberSelection(name),
      onBlur: () => rememberSelection(name),
      onSelect: () => rememberSelection(name),
      onKeyUp: () => rememberSelection(name),
      onClick: () => rememberSelection(name),
    }),
    [rememberSelection]
  );

  /**
   * Inserisce `text` nel campo attivo. Ritorna false se nessun campo è ancora
   * stato toccato: il chiamante può usarlo per avvisare l'utente.
   */
  const insertAtCursor = useCallback((text: string): boolean => {
    const name = activeFieldRef.current;
    if (!name) return false;
    const field = fieldsRef.current[name];
    const element = field?.element;
    if (!field || !element) return false;

    const currentValue = element.value;
    const start = Math.min(selectionRef.current.start, currentValue.length);
    const end = Math.min(selectionRef.current.end, currentValue.length);
    const nextValue = currentValue.slice(0, start) + text + currentValue.slice(end);
    const caret = start + text.length;

    field.setValue(nextValue);
    selectionRef.current = { start: caret, end: caret };

    requestAnimationFrame(() => {
      const target = fieldsRef.current[name]?.element;
      if (!target) return;
      target.focus();
      target.setSelectionRange(caret, caret);
    });

    return true;
  }, []);

  return { registerTarget, insertAtCursor };
};
